import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, Card } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import { showNotification } from "../../service/showNotification";
import {
  getAssetColumns,
  createTsaType,
  getContactGroup,
} from "../../service/api";
import { FaPlus, FaTrash } from "react-icons/fa";
import { tracker } from "../../service/mixpanel";
import { TSAConditionRow } from "./TsaConditionRow";

const CreateTsaModal = ({ onClose, onRefresh }) => {
  const [assetColumns, setAssetColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [contactGroupOptions, setContactGroupOptions] = useState([]);
  const [contactGroupLoading, setContactGroupLoading] = useState(false);
  const [contactGroupType, setContactGroupType] = useState(
    "Inception Financial"
  );
  const [conditions, setConditions] = useState([
    { columnId: "", operator: "", values: [], valueRange: [], dataType: "" },
  ]);
  const [conditionErrors, setConditionErrors] = useState({}); // Updated to store errors per field

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      yearlyRates: Array.from({ length: 21 }, (_, index) => ({
        year: index + 1,
        tsaRate: "",
        pcRate: "",
      })),
    },
  });

  const fetchColumns = async () => {
    try {
      setIsLoading(true);
      const res = await getAssetColumns();
      //  Filter out unwanted columns
      const filteredColumns = (res?.data || []).filter(
        (col) => !col.isDocument && col.label?.toLowerCase() !== "action"
      );

      setAssetColumns(filteredColumns ?? []);
    } catch (err) {
      console.log("err", err);
      showNotification("error", "Failed to fetch columns.");
    } finally {
      setIsLoading(false);
    }
  };

  const getContactGroupOptions = async () => {
    setContactGroupLoading(true);
    try {
      const response = await getContactGroup();
      const data = response.data.data || [];
      const transformed = data.map((item) => ({
        label: item.contactGroupName,
        value: item.contactGroupName,
      }));
      setContactGroupOptions(transformed);
    } catch {
      showNotification("error", "Failed to fetch contact groups.");
    } finally {
      setContactGroupLoading(false);
    }
  };
  useEffect(() => {
    fetchColumns();
    getContactGroupOptions();
  }, []);
  // Validate a single condition
  const validateCondition = (condition, index) => {
    const errors = {};
    if (!condition.columnId) {
      errors.columnId = "Column is required";
    }
    if (!condition.operator) {
      errors.operator = "Operator is required";
    }
    if (
      (!condition.values?.length && !condition.valueRange?.length) ||
      (condition.operator === "isBetween" &&
        (!condition.valueRange[0] || !condition.valueRange[1]))
    ) {
      errors.values = "Value(s) are required";
    }
    return errors;
  };

  // Update conditions and validate
  const handleConditionChange = (index, updated) => {
    const newConditions = [...conditions];
    newConditions[index] = updated;
    setConditions(newConditions);
  };

  const addCondition = () => {
    tracker.tsaManagement.addCondition();
    const newCondition = {
      columnId: "",
      operator: "",
      values: [],
      valueRange: [],
      dataType: "",
    };
    setConditions([...conditions, newCondition]);
  };

  const removeCondition = (index) => {
    tracker.tsaManagement.removeCondition(index);
    if (conditions.length > 1) {
      const newConditions = conditions.filter((_, i) => i !== index);
      setConditions(newConditions);

      const newErrors = { ...conditionErrors };
      delete newErrors[`${index}`];
      const reindexedErrors = {};
      Object.keys(newErrors).forEach((key) => {
        const idx = parseInt(key);
        if (idx > index) {
          reindexedErrors[`${idx - 1}`] = newErrors[key];
        } else {
          reindexedErrors[key] = newErrors[key];
        }
      });
      setConditionErrors(reindexedErrors);
    }
  };

  const [yearlyRatesError, setYearlyRatesError] = useState("");

  const validateYearlyRates = (data) => {
    if (contactGroupType === "AmpVantage Energy") {
      const firstSixYearsValid = data.yearlyRates
        .slice(0, 6)
        .every((r) => r.tsaRate && parseFloat(r.tsaRate) >= 0);
      if (!firstSixYearsValid) {
        setYearlyRatesError(
          "AmpVantage should have first 6 year record as using in Tranche"
        );
        return false;
      }
    }

    const validOneYear = data.yearlyRates.some(
      (r) =>
        r.tsaRate &&
        parseFloat(r.tsaRate) >= 0 &&
        r.pcRate &&
        parseFloat(r.pcRate) >= 0
    );

    if (!validOneYear) {
      setYearlyRatesError(
        "At least one year must have both TSA and P&C Rate filled"
      );
      return false;
    }

    const hasNegative = data.yearlyRates.some(
      (r) => parseFloat(r.tsaRate) < 0 || parseFloat(r.pcRate) < 0
    );

    if (hasNegative) {
      setYearlyRatesError("Rates cannot be negative");
      return false;
    }

    setYearlyRatesError("");
    return true;
  };

  const onSubmit = async (data) => {
    tracker.tsaManagement.clickCreateTsa();
    // Validate conditions
    const newErrors = {};
    conditions.forEach((condition, index) => {
      const errors = validateCondition(condition, index);
      if (Object.keys(errors).length > 0) {
        newErrors[`${index}`] = errors;
      }
    });

    setConditionErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      showNotification("error", "Please fill all condition fields");
      return;
    }

    // Validate yearly rates
    if (!validateYearlyRates(data)) {
      return;
    }

    const processedConditions = conditions.map((cond) => {
      const selectedColumn = assetColumns.find(
        (col) => col._id === cond.columnId
      );
      return {
        columnId: cond.columnId,
        columnName: selectedColumn?.label || cond.columnId,
        operator: cond.operator,
        ...(cond.operator === "isBetween"
          ? { valueRange: cond.values }
          : { values: cond.values }),
        dataType: selectedColumn?.dataType || "",
        valueRange: cond.valueRange,
        values: cond.values,
      };
    });

    const processedRates = data.yearlyRates
      .filter(
        (r) =>
          (r.tsaRate && parseFloat(r.tsaRate) >= 0) ||
          (r.pcRate && parseFloat(r.pcRate) >= 0)
      )
      .map((r) => ({
        year: r.year,
        tsaRate: parseFloat(r.tsaRate) || 0,
        pcRate: parseFloat(r.pcRate) || 0,
      }));

    const payload = {
      conditions: processedConditions,
      yearlyRates: processedRates,
      contactGroupType,
      isDefault: false,
      priority: 1,
    };

    try {
      setIsLoading(true);
      const response = await createTsaType(payload);
      showNotification(
        "success",
        response?.data?.message || "TSA Billing Type created successfully"
      );
      onRefresh();
      onClose();
    } catch (err) {
      console.error(err);
      showNotification(
        "error",
        err?.response?.data?.message || "Failed to create TSA billing type"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Basic Information</h6>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-0">
              Configure the conditions and yearly rates for this TSA billing
              type.
            </p>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Conditions</h6>
            <Button variant="outline-primary" size="sm" onClick={addCondition}>
              <FaPlus className="me-1" /> Add Condition
            </Button>
          </Card.Header>
          <Card.Body>
            {conditions.map((cond, index) => (
              <div key={index} className="d-flex align-items-start mb-3">
                <div className="flex-grow-1">
                  <TSAConditionRow
                    columns={assetColumns}
                    condition={cond}
                    onChange={(updated) =>
                      handleConditionChange(index, updated)
                    }
                    errors={conditionErrors[`${index}`] || {}}
                  />
                </div>
                {conditions.length > 1 && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="ms-2"
                    onClick={() => removeCondition(index)}
                    style={{
                      height: "38px",
                      width: "38px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "2rem",
                    }}
                  >
                    <FaTrash />
                  </Button>
                )}
              </div>
            ))}
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Select Contact Group Type</h6>
          </Card.Header>
          <Card.Body>
            <Row className="align-items-center">
              <Col md={2}>
                <Form.Label className="mb-0">Contact Group Type</Form.Label>
              </Col>
              <Col md={10}>
                <Select
                  placeholder="Select type"
                  onChange={(selected) => {
                    setContactGroupType(selected?.value || "");
                    tracker.tsaManagement.selectContactGroupType(
                      selected?.value
                    );
                  }}
                  value={
                    contactGroupOptions?.find(
                      (opt) => opt.value === contactGroupType
                    ) || null
                  }
                  defaultValue={
                    contactGroupOptions?.find(
                      (opt) => opt.value === "Inception Financial"
                    ) || null
                  }
                  options={contactGroupOptions}
                  isLoading={contactGroupLoading}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "38px",
                      height: "38px",
                      fontSize: "14px",
                      borderColor: "#ced4da",
                      borderRadius: "0.375rem",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      height: "36px",
                      padding: "0 12px",
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      height: "36px",
                    }),
                  }}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">
              Yearly Rates (21 Years) - At least 1 year must have both rates
              filled
            </h6>
          </Card.Header>
          <Card.Body>
            {yearlyRatesError && (
              <div className="alert alert-danger mb-3" role="alert">
                {yearlyRatesError}
              </div>
            )}
            <div
              style={{
                maxHeight: "400px",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <div style={{ width: "100%" }}>
                {Array.from({ length: 21 }, (_, index) => (
                  <Row
                    key={index}
                    className="mb-2 align-items-center"
                    style={{ margin: "0", width: "100%" }}
                  >
                    <Col
                      xs={12}
                      sm={4}
                      md={4}
                      style={{ paddingLeft: "0", paddingRight: "8px" }}
                    >
                      <Form.Label
                        className="mb-0"
                        style={{ fontSize: "14px", fontWeight: "500" }}
                      >
                        Year {index + 1}
                      </Form.Label>
                    </Col>
                    <Col
                      xs={6}
                      sm={4}
                      md={4}
                      style={{ paddingLeft: "8px", paddingRight: "8px" }}
                    >
                      <Controller
                        name={`yearlyRates.${index}.tsaRate`}
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="number"
                            step="0.000000000000001"
                            min="0"
                            placeholder="TSA Rate $"
                            size="sm"
                            style={{ width: "100%" }}
                            onChange={(e) => {
                              field.onChange(e);
                              setYearlyRatesError("");
                            }}
                            onKeyDown={(e) => {
                              if (
                                e.key === "-" ||
                                e.key === "e" ||
                                e.key === "E"
                              ) {
                                e.preventDefault();
                              }
                            }}
                          />
                        )}
                      />
                    </Col>
                    <Col
                      xs={6}
                      sm={4}
                      md={4}
                      style={{ paddingLeft: "8px", paddingRight: "0" }}
                    >
                      <Controller
                        name={`yearlyRates.${index}.pcRate`}
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            {...field}
                            type="number"
                            step="0.000000000000001"
                            min="0"
                            placeholder="P&C Rate $"
                            size="sm"
                            style={{ width: "100%" }}
                            onChange={(e) => {
                              field.onChange(e);
                              setYearlyRatesError("");
                            }}
                            onKeyDown={(e) => {
                              if (
                                e.key === "-" ||
                                e.key === "e" ||
                                e.key === "E"
                              ) {
                                e.preventDefault();
                              }
                            }}
                          />
                        )}
                      />
                    </Col>
                  </Row>
                ))}
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
        <Button
          variant="light"
          onClick={() => {
            tracker.tsaManagement.clickCancel();
            onClose();
          }}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={isLoading}>
          {isLoading ? "Creating..." : "Create TSA Type"}
        </Button>
      </div>
    </Form>
  );
};

export default CreateTsaModal;
